import _defineProperty from "/build/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { CONTRACTABLE_BUFFER, errorMessages } from "@/lib/constants";
import { CAMPAIGN_CODE_ERROR_REASONS, SUBSCRIPTION_ESTIMATE_ERROR_MESSAGES, VOUCHER_CODE_CHECK_ERROR_REASONS } from "@/lib/errors";
import { changePaymentInstrument } from "@/lib/mailTemplate";
import sendEmail from "@/lib/sendEmail";
import { gql } from "@apollo/client";
import { composeResolvers } from "@graphql-tools/resolvers-composition";
import { dateFormat, isAuthenticated, isDateRangeIn, logicaErrorBody, logicaErrorCode, ownerOnly, sleep } from "./helper";
export const typeDef = gql`
  type PaymentInstrumentValues {
    card_number: String
    card_type: String
  }
  type PaymentInstrument {
    values: PaymentInstrumentValues
  }

  type Entitlement {
    right_id: Int
  }

  type Subscription @cacheControl(scope: PRIVATE, maxAge: 0) {
    subscription_id: ID
    course_id: Int
    payment_method_id: Int
    payment_instrument_id: Int
    initialTransactionAt: String
    nextTransactionAt: String
    status: String
    course: Course
    payment_instrument: PaymentInstrument
    withdraw_at: String
    trialStartAt: String
    trialEndAt: String
    entitlements: [Entitlement]
    suspend_flag: Boolean
    init_order_id: Int
    orderPrice: Int
    error: String
  }

  type OptionsValue {
    label: String
    value: String
  }
  type FormQuestions {
    type: String
    label: String
    unique: String
    help_text: String
    options: [OptionsValue]
    key_name: String
    required: Boolean
    schema_group_name: String
  }

  type FormQuestionsResponse {
    question_id: Int
    data: [FormQuestions]
    values: JSON
  }

  type SubscriptionCancel @cacheControl(scope: PRIVATE) {
    course_id: Int
    status: String
    withdraw_at: String
    error: String
  }

  type FormQuestionAnswer {
    answer_id: Int
  }

  type SubscriptionCheckCancelableResponse {
    cancelable: Boolean
    subscription: SubscriptionCheckCancelableSubscription
  }

  type SubscriptionCheckCancelableSubscription {
    withdraw_at: String
  }

  type SubscriptionChangePaymentResult {
    payment_instrument_id: Int
    error: String
  }

  type SubscriptionStatusResponse @cacheControl(scope: PRIVATE, maxAge: 0) {
    is_contractable: Boolean
    is_contracted: Boolean
    have_be_contracted: Boolean
    is_trial_enabled: Boolean
    subscription: Subscription
  }

  type SubscriptionEstimate @cacheControl(scope: PRIVATE, maxAge: 0) {
    subscription: Subscription
    voucherCode: VoucherCode
    coursePricings: [CoursePrice]
    paymentPrice: CoursePrice
    # broadcastDiscountCampaignPrice: CoursePrice
    campaignPrice: CoursePrice
    error: String
  }

  type Query {
    subscriptions: [Subscription]

    subscriptionOne(id: Int): Subscription

    formQuestions(id: Int): FormQuestionsResponse @cacheControl(maxAge: 0)

    subscriptionCheckCancelable(
      subscriptionId: Int
    ): SubscriptionCheckCancelableResponse

    subscriptionStatus(courseId: Int): SubscriptionStatusResponse

    unSubscribedStatus(courseId: Int): Subscription

    subscriptionEstimate(
      courseId: Int
      paymentMethodId: Int
      couponCode: String
    ): SubscriptionEstimate
  }

  type Mutation {
    subscribe(
      courseId: Int
      paymentInstrumentId: Int
      voucherCode: String
      campaignCode: String
      valuePlanTerm: String
    ): Subscription

    subscriptionCancel(id: Int): SubscriptionCancel

    subscriptionChangePayment(
      paymentInstrumentId: Int!
      subscriptionId: Int!
    ): SubscriptionChangePaymentResult

    formQuestionAnswer(values: String, id: Int): FormQuestionAnswer
  }
`;
export const resolvers = composeResolvers({
  Query: {
    subscriptions: async (_parent, _args, _ref) => {
      var _result$subscriptions;
      let {
        req,
        dataSources
      } = _ref;
      const result = await dataSources.subscribe.subscription();
      const subscriptions = result === null || result === void 0 || (_result$subscriptions = result.subscriptions) === null || _result$subscriptions === void 0 ? void 0 : _result$subscriptions.filter(data => {
        var _data$course, _data$entitlements;
        return (data === null || data === void 0 || (_data$course = data.course) === null || _data$course === void 0 || (_data$course = _data$course.values) === null || _data$course === void 0 ? void 0 : _data$course.adult_flag) === false && (data === null || data === void 0 ? void 0 : data.entitlements) && (data === null || data === void 0 || (_data$entitlements = data.entitlements) === null || _data$entitlements === void 0 ? void 0 : _data$entitlements.length) !== 0;
      });
      return subscriptions;
    },
    subscriptionOne: async (_parent, _ref2, _ref3) => {
      let {
        id
      } = _ref2;
      let {
        dataSources
      } = _ref3;
      const res = await dataSources.subscribe.subscriptionOne(id);
      return res.subscription;
    },
    formQuestions: async (_parent, args, _ref4) => {
      var _res$question, _res$question2, _res$question3;
      let {
        dataSources
      } = _ref4;
      const res = await dataSources.subscribe.formQuestions(args);
      return {
        question_id: res === null || res === void 0 || (_res$question = res.question) === null || _res$question === void 0 ? void 0 : _res$question.question_id,
        data: res === null || res === void 0 || (_res$question2 = res.question) === null || _res$question2 === void 0 ? void 0 : _res$question2.schema,
        values: res === null || res === void 0 || (_res$question3 = res.question) === null || _res$question3 === void 0 ? void 0 : _res$question3.values
      };
    },
    subscriptionCheckCancelable: async (_parent, _ref5, _ref6) => {
      let {
        subscriptionId
      } = _ref5;
      let {
        dataSources
      } = _ref6;
      try {
        const res = await dataSources.subscribe.subscriptionCheckCancelable(subscriptionId);
        return res[subscriptionId];
      } catch (e) {
        console.log("subscribe check cancelable error", e);
      }
    },
    subscriptionStatus: async (_parent, _ref7, _ref8) => {
      let {
        courseId
      } = _ref7;
      let {
        dataSources
      } = _ref8;
      try {
        var _res$subscriptions;
        const res = await dataSources.subscribe.subscriptionStatus(courseId);
        const _subscription = (_res$subscriptions = res.subscriptions) === null || _res$subscriptions === void 0 ? void 0 : _res$subscriptions[0];
        const getSubscription = async subscriptionId => {
          if (subscriptionId) {
            const subscription = await dataSources.subscribe.subscriptionOne(subscriptionId);
            return subscription === null || subscription === void 0 ? void 0 : subscription.subscription;
          } else {
            return null;
          }
        };
        const subscription = await getSubscription(_subscription === null || _subscription === void 0 ? void 0 : _subscription.subscription_id);
        return _objectSpread(_objectSpread({}, res), {}, {
          subscription: subscription
        });
      } catch (e) {
        console.log("subscriptionStatus error", e);
      }
    },
    unSubscribedStatus: async (_parent, _ref9, _ref10) => {
      let {
        courseId
      } = _ref9;
      let {
        dataSources
      } = _ref10;
      try {
        var _res$subscriptions2;
        const res = await dataSources.subscribe.subscriptionStatus(courseId);
        const _subscription = (_res$subscriptions2 = res.subscriptions) === null || _res$subscriptions2 === void 0 ? void 0 : _res$subscriptions2[0];
        const getSubscription = async subscriptionId => {
          if (subscriptionId) {
            const subscription = await dataSources.subscribe.subscriptionOne(subscriptionId);
            return subscription === null || subscription === void 0 ? void 0 : subscription.subscription;
          } else {
            return null;
          }
        };
        const subscription = await getSubscription(_subscription === null || _subscription === void 0 ? void 0 : _subscription.subscription_id);
        if (subscription) {
          console.log("while subscribing");
          return null;
        } else {
          return res === null || res === void 0 ? void 0 : res.subscriptions[0];
        }
      } catch (e) {
        console.log("unSubscribedStatus error", e);
      }
    },
    subscriptionEstimate: async (_parent, _ref11, _ref12) => {
      let {
        courseId,
        paymentMethodId,
        couponCode
      } = _ref11;
      let {
        dataSources
      } = _ref12;
      try {
        const res = await dataSources.subscribe.estimate(courseId, paymentMethodId, couponCode);
        return {
          subscription: res === null || res === void 0 ? void 0 : res.subscription,
          voucherCode: res === null || res === void 0 ? void 0 : res.voucher_code,
          coursePricings: res === null || res === void 0 ? void 0 : res.course_pricings,
          paymentPrice: res === null || res === void 0 ? void 0 : res.payment_pricing,
          campaigns: res === null || res === void 0 ? void 0 : res.campaigns
        };
      } catch (e) {
        var _body$error, _body$error2;
        console.log("subscriptionEstimate error", e);
        const body = logicaErrorBody(e);
        const errorCode = body === null || body === void 0 || (_body$error = body.error) === null || _body$error === void 0 ? void 0 : _body$error.code;
        const reason = body === null || body === void 0 || (_body$error2 = body.error) === null || _body$error2 === void 0 ? void 0 : _body$error2.reason_type;
        const {
          code,
          message
        } = (() => {
          if (errorCode === 22001 && reason && VOUCHER_CODE_CHECK_ERROR_REASONS[reason]) {
            return VOUCHER_CODE_CHECK_ERROR_REASONS[reason];
          } else if (errorCode === 34001 && reason && CAMPAIGN_CODE_ERROR_REASONS[reason]) {
            return CAMPAIGN_CODE_ERROR_REASONS[reason];
          }
          return {
            code: errorCode,
            message: SUBSCRIPTION_ESTIMATE_ERROR_MESSAGES[errorCode] || errorMessages["subscriptionEstimate"]
          };
        })();
        return {
          error: `${message} エラーコード: ${code}`
        };
      }
    }
  },
  Mutation: {
    subscribe: async (_parent, _ref13, _ref14) => {
      let {
        courseId,
        paymentInstrumentId,
        voucherCode,
        campaignCode,
        valuePlanTerm
      } = _ref13;
      let {
        req,
        dataSources
      } = _ref14;
      try {
        var _req$userToken, _orderData;
        // コースを取得して契約期間を確認
        const course = await dataSources.courses.courseById(courseId);
        const contractAllowed = isDateRangeIn(course === null || course === void 0 ? void 0 : course.contractable_start_at, course === null || course === void 0 ? void 0 : course.contractable_end_at, {
          buffer: CONTRACTABLE_BUFFER,
          default: true
        });
        if (!contractAllowed) {
          return {
            error: "契約可能期間外のため購入できません。"
          };
        }

        // キャンペーンコードがある場合は、applyAPIをコール
        if (campaignCode) {
          console.log(`apply couponCode: ${campaignCode}`);
          await dataSources.subscribe.applyCampaign(courseId, campaignCode);
        }
        const res = await dataSources.subscribe.subscribe(courseId, paymentInstrumentId, voucherCode);
        const userId = req === null || req === void 0 || (_req$userToken = req.userToken) === null || _req$userToken === void 0 ? void 0 : _req$userToken.id;
        console.log(`########## Subscribe, userId:${userId}, courseId: ${courseId} ##########`);
        console.log("subscribe", res);
        console.log("########## Subscribe End ##########");
        await sleep(200);
        // バリュープランだったら規約に同意させる
        if (valuePlanTerm) {
          await dataSources.terms.agree(valuePlanTerm);
        }
        const orderId = res.subscription.init_order_id;
        let orderData = {};
        if (!!orderId) {
          orderData = await dataSources.orders.getOrderOne(orderId);
        }
        const orderPrice = ((_orderData = orderData) === null || _orderData === void 0 ? void 0 : _orderData.payment_amount) || 0;
        const subscription = res.subscription;
        // TODO: トライアルなしオーダー（init_order_id）が存在しない場合のエラーログ、通知を追加する
        return _objectSpread(_objectSpread({}, subscription), {}, {
          orderPrice
        });
      } catch (e) {
        console.log("subscribe error", e);
        const errorCode = logicaErrorCode(e);
        return {
          error: `${errorMessages["subscribeExecute"]} エラーコード: ${errorCode}`
        };
      }
    },
    subscriptionCancel: async (_parent, _ref15, _ref16) => {
      let {
        id
      } = _ref15;
      let {
        req,
        dataSources
      } = _ref16;
      try {
        var _req$userToken2;
        const res = await dataSources.subscribe.subscriptionCancel(id);
        const userId = req === null || req === void 0 || (_req$userToken2 = req.userToken) === null || _req$userToken2 === void 0 ? void 0 : _req$userToken2.id;
        console.log(`########## subscriptionCancel, userId:${userId}, courseId: ${id} ##########`);
        console.log("subscriptionCancel", res);
        return res.subscription;
      } catch (e) {
        var _e$extensions;
        console.log("subscribe cancel error", e);
        const message = e === null || e === void 0 || (_e$extensions = e.extensions) === null || _e$extensions === void 0 || (_e$extensions = _e$extensions.response) === null || _e$extensions === void 0 || (_e$extensions = _e$extensions.body) === null || _e$extensions === void 0 || (_e$extensions = _e$extensions.error) === null || _e$extensions === void 0 ? void 0 : _e$extensions.message;
        const errorCode = logicaErrorCode(e);
        return {
          error: `${message} エラーコード: ${errorCode}`
        };
      }
    },
    formQuestionAnswer: async (_parent, _ref17, _ref18) => {
      let {
        id,
        values
      } = _ref17;
      let {
        dataSources
      } = _ref18;
      try {
        const res = await dataSources.subscribe.formQuestionAnswer(id, values);
        return res.answers;
      } catch (e) {
        console.log("form question answer error", e);
      }
    },
    subscriptionChangePayment: async (_parent, args, _ref19) => {
      let {
        req,
        dataSources
      } = _ref19;
      try {
        const {
          subscription
        } = await dataSources.subscribe.subscriptionChangePayment(args);
        const {
          payment_instrument: paymentInstrument
        } = await dataSources.paymentInstruments.paymentInstrument(subscription.payment_instrument_id);
        const course = await dataSources.courses.courseById(subscription.course_id);
        // メール送信
        const {
          user
        } = await dataSources.user.me();
        await sendEmail(changePaymentInstrument({
          address: user.mail_address,
          paymentInstrument,
          course
        }));
        return subscription;
      } catch (e) {
        console.log("subscribe change payment error", e);
        const errorCode = logicaErrorCode(e);
        return {
          error: `${errorMessages["subscriptionChangePayment"]} エラーコード: ${errorCode}`
        };
      }
    }
  },
  Subscription: {
    initialTransactionAt: sub => dateFormat(sub.initial_transaction_at),
    nextTransactionAt: sub => dateFormat(sub.next_transaction_at),
    trialStartAt: sub => dateFormat(sub.trial_start_at),
    trialEndAt: sub => dateFormat(sub.trial_end_at)
  },
  SubscriptionEstimate: {
    // broadcastDiscountCampaignPrice: ({ coursePricings, campaigns }) => {
    //   // TODO: 本番のAPIが更新されてcourse_pricingsにcampaign情報が入るようになったらcoursePricingsで直接見る
    //   // 設定されている放送契約割引キャンペーン
    //   if (!coursePricings && !campaigns) return null;
    //   const campaign = availableCamapigns(coursePricings, campaigns).find(
    //     (campaign) => campaign?.values?.is_broadcast_discount
    //   );
    //   console.log(coursePricings, campaigns);
    //   if (!campaign) return null;
    //   const price = coursePricings.find((cp) => {
    //     const campaignId = cp?.campaign_id || cp?.campaign?.campaign_id;
    //     return Number(campaignId) === Number(campaign?.campaign_id);
    //   });
    //   return price;
    // },
    campaignPrice: _ref20 => {
      var _paymentPrice$campaig;
      let {
        paymentPrice
      } = _ref20;
      // 適用された放送契約割引ではないキャンペーン
      if (paymentPrice !== null && paymentPrice !== void 0 && paymentPrice.campaign && !(paymentPrice !== null && paymentPrice !== void 0 && (_paymentPrice$campaig = paymentPrice.campaign) !== null && _paymentPrice$campaig !== void 0 && (_paymentPrice$campaig = _paymentPrice$campaig.values) !== null && _paymentPrice$campaig !== void 0 && _paymentPrice$campaig.is_broadcast_discount)) {
        return paymentPrice;
      }
      return null;
    }
  }
}, {
  "Mutation.subscribe": [isAuthenticated(), ownerOnly()],
  "Mutation.subscriptionCancel": [isAuthenticated(), ownerOnly()],
  "Mutation.subscriptionChangePayment": [isAuthenticated(), ownerOnly()],
  "Mutation.formQuestionAnswer": [isAuthenticated(), ownerOnly()]
});

/**
 * availableCamapigns.
 * estimateAPIの仕様変更対応
 * APIのレスポンスからcampaignsが廃止され、course_pricingsにcampaignが含まれるようになる
 * APIリリース時にエラーにならないようにどちらの場合でも動作するようにする
 * 後に新しい仕様に寄せる
 *
 * @param {} course_pricings
 * @param {} campaigns
 */
function availableCamapigns(coursePricings, campaigns) {
  if (campaigns) return campaigns;
  if (!coursePricings) return [];
  return coursePricings.filter(cp => cp.campaign).map(cp => cp.campaign);
}